import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import {
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  Info,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Shield,
  ClipboardList,
  Boxes,
  Workflow,
  FileSpreadsheet,
  Bell,
  Smartphone,
  Eye,
  Database,
  Mail,
  Map as MapIcon,
  ServerCog,
  Cloud,
  MessageSquare,
  Brain,
  Layers,
  Server,
  ArrowLeft,
  ExternalLink,
  X,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const S = { DONE: "done", WIP: "wip", PENDING: "pending" };
const STATUS_META = {
  [S.DONE]:    { label: "Implementado", color: "#16A34A", bg: "#DCFCE7", border: "#22C55E", Icon: CheckCircle2 },
  [S.WIP]:     { label: "En progreso",  color: "#B45309", bg: "#FEF3C7", border: "#F59E0B", Icon: Clock },
  [S.PENDING]: { label: "Pendiente",    color: "#B91C1C", bg: "#FEE2E2", border: "#EF4444", Icon: XCircle },
};

const NODE_W = 220;
const NODE_H = 80;
const CANVAS_W = 1500;
const CANVAS_H = 820;

// ─── MÓDULOS (vista macro) ─────────────────────────────────────────────────
const MODULES = [
  {
    id: "M17", label: "M17 · Seguridad y Acceso", icon: Shield, x: 220, y: 130,
    description: "Autenticación, roles, MFA, audit log. Gate de acceso a todos los módulos.",
    internal: [
      { id: "login",  label: "Login JWT + MFA TOTP",   status: S.DONE, detail: "Access 8h · Refresh 30d · 10 backup codes" },
      { id: "roles",  label: "9 Roles + Permisos",     status: S.DONE, detail: "super_admin, admin_zone, supervisor, fiscalizer, finance, technician, contractor, support, auditor" },
      { id: "audit",  label: "Audit Log inmutable",    status: S.DONE, detail: "Append-only · 9 tipos de eventos" },
      { id: "lock",   label: "Password Policy + Lockout", status: S.DONE, detail: "10+ chars · 5 intentos → 15 min bloqueo" },
      { id: "pwdexp", label: "Expiración Password 180d", status: S.PENDING, detail: "Cron job pendiente de implementar" },
    ],
  },
  {
    id: "M1", label: "M1 · Órdenes de Trabajo", icon: ClipboardList, x: 750, y: 130,
    description: "Ciclo de vida completo: creación, planificación, ejecución y cierre.",
    internal: [
      { id: "create",  label: "Crear Orden",           status: S.DONE, detail: "Cliente + zona + tipo + prioridad + materiales" },
      { id: "geocode", label: "Geocoding automático",  status: S.DONE, detail: "OpenStreetMap Nominatim" },
      { id: "antidup", label: "Anti-duplicado 24h",    status: S.DONE, detail: "Bloquea cliente + tipo repetido" },
      { id: "code",    label: "Código OT-AAAAMMDD-N", status: S.DONE, detail: "Generación única automática" },
      { id: "prefeas", label: "RN-16 · Pre-factibilidad", status: S.WIP, detail: "Valida cobertura + stock + capacidad" },
      { id: "massevt", label: "RN-13 · Bloqueo Evento Masivo", status: S.WIP, detail: "No permite agendar en zona con evento activo" },
      { id: "assign",  label: "Asignación técnico",    status: S.DONE, detail: "Selección técnico + contratista" },
      { id: "sla",     label: "SLA por prioridad",     status: S.DONE, detail: "P1=4h · P2=24h · P3=72h" },
      { id: "dsla",    label: "RN-01 · SLA Dinámico",  status: S.WIP, detail: "SLA por tipo + zona + prioridad" },
      { id: "fsm",     label: "Máquina de 12 estados", status: S.DONE, detail: "Transiciones validadas + histórico" },
      { id: "close",   label: "Cierre de orden",       status: S.DONE, detail: "Motivo + histórico + audit log" },
      { id: "return",  label: "RN-17 · Devolución obligatoria", status: S.DONE, detail: "Serial + condición obligatorios en órdenes retiro/upgrade antes de fiscalización · IMPLEMENTADO Feb 2026" },
      { id: "nc",      label: "No Conformidades",      status: S.WIP, detail: "Migrar de MVP legacy a H1" },
    ],
    relatedFlows: [
      { id: "ciclo-ot", title: "Ciclo de Vida de una OT" },
      { id: "alertas-resolucion", title: "Alertas y Resolución" },
      { id: "asignacion-inteligente", title: "Asignación Inteligente de Cuadrillas" },
    ],
  },
  {
    id: "M6", label: "M6 · Inventario y Materiales", icon: Boxes, x: 1250, y: 130,
    description: "Stock por sucursal + asignación a órdenes con descuento automático.",
    internal: [
      { id: "inv",       label: "Stock por sucursal (8)", status: S.DONE, detail: "Ajustes, movimientos, saldos" },
      { id: "matassign", label: "Materiales × Orden",     status: S.DONE, detail: "Descuento automático + reversión" },
      { id: "matctrl",   label: "RN-08 · Control estricto", status: S.DONE, detail: "Validación saldo + branch del técnico + serial obligatorio · IMPLEMENTADO Feb 2026" },
      { id: "infra",     label: "RN-09 · Órdenes de Infraestructura", status: S.DONE, detail: "Nueva entidad de reposición interna · draft→approved→fulfilled aumenta stock destino · IMPLEMENTADO Feb 2026" },
    ],
  },
  {
    id: "M7", label: "M7 · Catálogos y Configuración", icon: Workflow, x: 220, y: 380,
    description: "Tipos de solución con flujos + zonas operativas + parametrización.",
    internal: [
      { id: "sol",   label: "Tipos de Solución (29)", status: S.DONE, detail: "Diagramas SVG dinámicos + PDF batch" },
      { id: "zonas", label: "Zonas Operativas (5)",   status: S.DONE, detail: "Geocoding automático" },
      { id: "pri",   label: "Prioridades y Tipos OT", status: S.DONE, detail: "P1/P2/P3 + catálogo tipos" },
    ],
  },
  {
    id: "M20", label: "M20 · Reportes y Búsqueda", icon: FileSpreadsheet, x: 750, y: 380,
    description: "Exportes, reportería y búsquedas transversales.",
    internal: [
      { id: "excel", label: "Reportes Excel",              status: S.DONE, detail: "Exportes automatizados del sistema" },
      { id: "sign",  label: "RN-02 · Firma SHA-256",       status: S.WIP, detail: "Hash + timestamp en PDF/Excel" },
      { id: "phone", label: "RN-05 · Teléfonos históricos", status: S.WIP, detail: "Búsqueda por cualquier tel. asociado" },
      { id: "bi",    label: "Dashboard BI H1",              status: S.PENDING, detail: "Migrar KPIs del MVP legacy" },
    ],
    relatedFlows: [
      { id: "tres-firmas", title: "Pre-Facturación (3 Firmas)" },
      { id: "diseno-expansion", title: "Diseño y Expansión de Red" },
    ],
  },
  {
    id: "NOTIF", label: "Notificaciones", icon: Bell, x: 1250, y: 380,
    description: "Emails y mensajería saliente.",
    internal: [
      { id: "mailgun", label: "Mailgun SMTP",   status: S.DONE, detail: "Emails de notificación operativos" },
      { id: "wa",      label: "WhatsApp real",  status: S.PENDING, detail: "Mock actual · integrar Twilio o Meta API" },
      { id: "purge",   label: "Purga logs (cron)", status: S.PENDING, detail: "Retención automática" },
    ],
  },
  {
    id: "MOV", label: "Movilidad y Evidencias", icon: Smartphone, x: 400, y: 620,
    description: "App móvil de campo con evidencias y geofencing.",
    internal: [
      { id: "app",     label: "App iOS/Android",  status: S.PENDING, detail: "Cliente móvil con geofencing" },
      { id: "evid",    label: "Captura evidencias", status: S.PENDING, detail: "Fotos + firma + GPS" },
      { id: "offline", label: "Modo offline",     status: S.PENDING, detail: "Sync diferido" },
    ],
  },
  {
    id: "FISC", label: "Fiscalización Vision AI", icon: Eye, x: 1070, y: 620,
    description: "Validación automática de evidencias con IA.",
    internal: [
      { id: "vision",  label: "Análisis con Claude Vision", status: S.PENDING, detail: "Validación de fotos de instalación" },
      { id: "verdict", label: "Verdict + confianza %",      status: S.PENDING, detail: "Validado / observación / pendiente" },
    ],
    relatedFlows: [
      { id: "fiscalizacion-ia", title: "Fiscalización con Vision AI" },
    ],
  },
];

const MODULE_EDGES = [
  ["M17", "M1"], ["M17", "M6"], ["M17", "M7"], ["M17", "M20"],
  ["M7", "M1"],
  ["M1", "M6"],
  ["M1", "M20"], ["M1", "NOTIF"], ["M1", "MOV"], ["M1", "FISC"],
  ["MOV", "FISC"],
];

// ─── INTEGRACIONES EXTERNAS ────────────────────────────────────────────────
const INTEGRATIONS = [
  {
    id: "AFC", label: "Alfa-Field Connect", icon: Server, x: 750, y: 400,
    status: S.DONE, isHub: true,
    description: "Núcleo del sistema. FastAPI + React + PostgreSQL.",
    internal: [
      { id: "api",  label: "FastAPI Backend",         status: S.DONE, detail: "Endpoints /api/v1/* + MVP legacy" },
      { id: "web",  label: "React Frontend",          status: S.DONE, detail: "React 19 + Shadcn + Tailwind" },
      { id: "jwt",  label: "JWT + Refresh Tokens",     status: S.DONE, detail: "Auto-refresh en axios interceptor" },
    ],
  },
  {
    id: "PG", label: "AWS RDS PostgreSQL", icon: Database, x: 220, y: 150,
    status: S.DONE,
    description: "Persistencia principal 100% PG. Adapter mongo-like para compatibilidad.",
    internal: [
      { id: "schema", label: "Schema PostgreSQL",       status: S.DONE, detail: "work_orders, inventory, solution_types, audit_log, users, roles..." },
      { id: "adapter", label: "Adapter mongo_like.py",  status: S.DONE, detail: "Mapea sintaxis motor → PG queries" },
      { id: "backup",  label: "Backups automáticos AWS", status: S.DONE, detail: "RDS snapshots gestionados" },
    ],
  },
  {
    id: "MG", label: "Mailgun SMTP", icon: Mail, x: 750, y: 130,
    status: S.DONE,
    description: "Servicio SMTP transaccional para notificaciones.",
    internal: [
      { id: "smtp",   label: "SMTP relay",              status: S.DONE, detail: "Credenciales cliente configuradas" },
      { id: "tpl",    label: "Templates de email",      status: S.DONE, detail: "Notif. de cambio de estado + asignación" },
      { id: "audit-mg", label: "Registro de envíos",    status: S.DONE, detail: "Trazabilidad de emails enviados" },
    ],
  },
  {
    id: "OSM", label: "OpenStreetMap Nominatim", icon: MapIcon, x: 1280, y: 150,
    status: S.DONE,
    description: "Servicio gratuito de geocoding (dirección → lat/lng).",
    internal: [
      { id: "geo",   label: "Geocoding directo",       status: S.DONE, detail: "Direcciones → coordenadas al crear OT" },
      { id: "rate",  label: "Rate limiting propio",    status: S.DONE, detail: "Respeto de política free tier" },
    ],
  },
  {
    id: "ODOO", label: "Odoo ERP · XML-RPC", icon: ServerCog, x: 1280, y: 650,
    status: S.PENDING,
    description: "ERP del cliente. Sync bidireccional de productos e inventario.",
    internal: [
      { id: "prod",  label: "product.product",         status: S.PENDING, detail: "Sync de catálogo maestro" },
      { id: "quant", label: "stock.quant",             status: S.PENDING, detail: "Saldos y ubicaciones" },
      { id: "creds", label: "Credenciales cliente",    status: S.PENDING, detail: "🔒 Bloqueado — pendiente entrega" },
    ],
  },
  {
    id: "R2", label: "Cloudflare R2", icon: Cloud, x: 750, y: 680,
    status: S.PENDING,
    description: "Object storage para evidencias fotográficas de campo.",
    internal: [
      { id: "bucket", label: "Bucket de evidencias",   status: S.PENDING, detail: "Upload multipart desde app móvil" },
      { id: "sig",    label: "URLs firmadas",          status: S.PENDING, detail: "Acceso temporal seguro" },
    ],
  },
  {
    id: "TW", label: "Twilio / Meta WhatsApp", icon: MessageSquare, x: 220, y: 650,
    status: S.PENDING,
    description: "Mensajería WhatsApp real (actualmente mock).",
    internal: [
      { id: "wa-api",  label: "WhatsApp Business API", status: S.PENDING, detail: "Notif. a clientes + técnicos" },
      { id: "tpl-wa",  label: "Templates aprobados",   status: S.PENDING, detail: "Formatos oficiales Meta" },
    ],
  },
  {
    id: "AI", label: "Claude Vision AI", icon: Brain, x: 220, y: 400,
    status: S.PENDING,
    description: "Análisis de imágenes para fiscalización automática.",
    internal: [
      { id: "vis-api", label: "Anthropic Vision API",  status: S.PENDING, detail: "Validación de fotos de instalación" },
      { id: "prompt",  label: "Prompts de fiscalización", status: S.PENDING, detail: "Reglas por tipo de instalación" },
    ],
  },
];

const INTEGRATION_EDGES = [
  ["PG",   "AFC"], ["MG",  "AFC"], ["OSM",  "AFC"],
  ["ODOO", "AFC"], ["R2",  "AFC"], ["TW",   "AFC"], ["AI", "AFC"],
];

// ─── Utilidades ────────────────────────────────────────────────────────────
function aggregateStatus(internal) {
  const counts = { done: 0, wip: 0, pending: 0 };
  internal.forEach((n) => counts[n.status]++);
  if (counts.done === internal.length) return S.DONE;
  if (counts.pending === internal.length) return S.PENDING;
  return S.WIP;
}

function edgePath(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let x1, y1, x2, y2;
  if (Math.abs(dx) > Math.abs(dy)) {
    x1 = a.x + (dx > 0 ? NODE_W / 2 : -NODE_W / 2); y1 = a.y;
    x2 = b.x + (dx > 0 ? -NODE_W / 2 : NODE_W / 2); y2 = b.y;
    const mx = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
  }
  x1 = a.x; y1 = a.y + (dy > 0 ? NODE_H / 2 : -NODE_H / 2);
  x2 = b.x; y2 = b.y + (dy > 0 ? -NODE_H / 2 : NODE_H / 2);
  const my = (y1 + y2) / 2;
  return `M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`;
}

// ─── Página principal ─────────────────────────────────────────────────────
export default function FlujoSistema() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("modules"); // 'modules' | 'integrations'
  const [drill, setDrill] = useState(null); // módulo/integración clickeado
  const [selectedInternal, setSelectedInternal] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  const [exporting, setExporting] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const flowRef = useRef(null);

  // Enriquecemos módulos con status agregado
  const modulesWithStatus = useMemo(
    () => MODULES.map((m) => ({ ...m, status: aggregateStatus(m.internal) })),
    []
  );

  const currentNodes = tab === "modules" ? modulesWithStatus : INTEGRATIONS;
  const currentEdges = tab === "modules" ? MODULE_EDGES : INTEGRATION_EDGES;

  const counts = useMemo(() => {
    const c = { done: 0, wip: 0, pending: 0, total: currentNodes.length };
    currentNodes.forEach((n) => c[n.status]++);
    return c;
  }, [currentNodes]);
  const pct = (n) => Math.round((n / counts.total) * 100);

  const nodeById = (id) => currentNodes.find((n) => n.id === id);
  const neighborsOf = (id) => {
    const s = new Set();
    currentEdges.forEach(([a, b]) => { if (a === id) s.add(b); if (b === id) s.add(a); });
    return s;
  };
  const highlightSet = hoverId ? neighborsOf(hoverId).add(hoverId) : null;

  const handleExportPDF = async () => {
    if (!flowRef.current) return;
    setExporting(true);
    toast.loading("Generando PDF...", { id: "pdf-flow" });
    try {
      const canvas = await html2canvas(flowRef.current, { scale: 2, backgroundColor: "#F8FAFC", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      pdf.setFontSize(16); pdf.setTextColor(10, 55, 71);
      pdf.text(`Alfa-Field Connect · ${tab === "modules" ? "Módulos del Sistema" : "Integraciones Externas"}`, 10, 12);
      pdf.setFontSize(9); pdf.setTextColor(100);
      pdf.text(`TELEALFACOM S.A. · ${new Date().toLocaleDateString("es-EC")}`, 10, 17);
      const imgW = pageW - 20;
      const imgH = (canvas.height * imgW) / canvas.width;
      const finalH = Math.min(imgH, pageH - 25);
      const finalW = imgH > pageH - 25 ? (canvas.width * (pageH - 25)) / canvas.height : imgW;
      pdf.addImage(img, "PNG", (pageW - finalW) / 2, 22, finalW, finalH);
      pdf.save(`Alfa-Field_${tab}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF descargado", { id: "pdf-flow" });
    } catch (e) {
      console.error(e); toast.error("Error generando PDF", { id: "pdf-flow" });
    } finally { setExporting(false); }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="flujo-sistema-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-mono mb-1">
              Documentación · Arquitectura funcional
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>
              Flujo del Sistema
            </h1>
            <p className="text-gray-600 mt-1 text-sm max-w-3xl">
              Vista macro de los módulos y las integraciones externas de Alfa-Field Connect.
              Haz clic en un módulo o sistema para ver los nodos internos que lo componen.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))} className="p-2 rounded-md bg-white border border-gray-300 hover:bg-gray-50" title="Acercar" data-testid="zoom-in-btn"><ZoomIn size={16} /></button>
            <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))} className="p-2 rounded-md bg-white border border-gray-300 hover:bg-gray-50" title="Alejar" data-testid="zoom-out-btn"><ZoomOut size={16} /></button>
            <button onClick={() => setZoom(0.85)} className="p-2 rounded-md bg-white border border-gray-300 hover:bg-gray-50" title="Reset" data-testid="zoom-reset-btn"><Maximize2 size={16} /></button>
            <button onClick={handleExportPDF} disabled={exporting} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A3747] text-white text-sm hover:bg-[#135166] transition disabled:opacity-50" data-testid="export-flow-pdf-btn">
              <Download size={14} />{exporting ? "Generando..." : "PDF"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1 w-fit">
          <TabButton active={tab === "modules"} onClick={() => setTab("modules")} icon={Layers} label="Módulos" testid="tab-modules" />
          <TabButton active={tab === "integrations"} onClick={() => setTab("integrations")} icon={Cloud} label="Integraciones Externas" testid="tab-integrations" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatBox label={tab === "modules" ? "Módulos" : "Sistemas"} value={counts.total} tone="neutral" />
          <StatBox label="Operativos" value={`${counts.done} · ${pct(counts.done)}%`} tone={S.DONE} />
          <StatBox label="En progreso" value={`${counts.wip} · ${pct(counts.wip)}%`} tone={S.WIP} />
          <StatBox label="Pendientes" value={`${counts.pending} · ${pct(counts.pending)}%`} tone={S.PENDING} />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-5 text-[11px] font-mono text-gray-600">
            <LegendDot color="#22C55E" label="Operativo" />
            <LegendDot color="#F59E0B" label="En progreso (Fase 1)" />
            <LegendDot color="#EF4444" label="Pendiente / Bloqueado" />
          </div>
          <div className="h-2 flex-1 max-w-md rounded-full overflow-hidden bg-gray-100 flex">
            <div className="h-full bg-[#22C55E]" style={{ width: `${pct(counts.done)}%` }} />
            <div className="h-full bg-[#F59E0B]" style={{ width: `${pct(counts.wip)}%` }} />
            <div className="h-full bg-[#EF4444]" style={{ width: `${pct(counts.pending)}%` }} />
          </div>
        </div>

        {/* Canvas */}
        <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg overflow-auto" style={{ maxHeight: "78vh" }}>
          <div
            ref={flowRef}
            className="relative"
            style={{
              width: CANVAS_W * zoom, height: CANVAS_H * zoom,
              backgroundImage: "radial-gradient(circle, #CBD5E1 1px, transparent 1px)",
              backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
            }}
          >
            <div style={{ width: CANVAS_W, height: CANVAS_H, transform: `scale(${zoom})`, transformOrigin: "0 0", position: "relative" }}>
              <svg width={CANVAS_W} height={CANVAS_H} className="absolute inset-0 pointer-events-none">
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#94A3B8" />
                  </marker>
                  <marker id="arrow-active" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#0A3747" />
                  </marker>
                </defs>
                {currentEdges.map(([fromId, toId], i) => {
                  const a = nodeById(fromId); const b = nodeById(toId);
                  if (!a || !b) return null;
                  const active = highlightSet && highlightSet.has(fromId) && highlightSet.has(toId);
                  return (
                    <path key={i} d={edgePath(a, b)} fill="none"
                      stroke={active ? "#0A3747" : "#94A3B8"}
                      strokeWidth={active ? 2.6 : 1.6}
                      strokeDasharray={tab === "integrations" && (a.status === S.PENDING || b.status === S.PENDING) ? "6,4" : "0"}
                      opacity={highlightSet && !active ? 0.15 : 0.85}
                      markerEnd={active ? "url(#arrow-active)" : "url(#arrow)"}
                    />
                  );
                })}
              </svg>

              {currentNodes.map((node) => {
                const meta = STATUS_META[node.status];
                const { Icon } = meta;
                const dim = highlightSet && !highlightSet.has(node.id);
                const NodeIcon = node.icon || Server;
                const isHub = node.isHub;
                return (
                  <button
                    key={node.id}
                    onClick={() => setDrill(node)}
                    onMouseEnter={() => setHoverId(node.id)}
                    onMouseLeave={() => setHoverId(null)}
                    data-testid={`node-${node.id}`}
                    className="absolute rounded-xl text-left transition-all"
                    style={{
                      left: node.x - NODE_W / 2, top: node.y - NODE_H / 2,
                      width: NODE_W, height: NODE_H,
                      background: isHub ? "#0A3747" : meta.bg,
                      border: `2px solid ${isHub ? "#E0E621" : meta.border}`,
                      boxShadow: hoverId === node.id ? "0 10px 26px rgba(10,55,71,0.28)" : "0 1px 3px rgba(0,0,0,0.08)",
                      opacity: dim ? 0.35 : 1,
                      transform: hoverId === node.id ? "scale(1.05)" : "scale(1)",
                    }}
                  >
                    <div className="flex items-center gap-3 px-3 py-2 h-full">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: isHub ? "#E0E621" : meta.color }}>
                        <NodeIcon size={20} color={isHub ? "#0A3747" : "#fff"} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[10px] font-mono uppercase tracking-wider leading-none ${isHub ? "text-[#E0E621]" : "text-gray-500"}`}>
                          {isHub ? "Core" : STATUS_META[node.status].label}
                        </div>
                        <div className={`text-[13px] font-semibold leading-tight mt-1 ${isHub ? "text-white" : "text-gray-900"}`}
                          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {node.label}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 flex gap-3 text-sm">
          <Info size={20} className="text-[#0A3747] flex-shrink-0 mt-0.5" />
          <div className="text-gray-700 leading-relaxed">
            {tab === "modules" ? (
              <>Cada bloque es un <strong>módulo funcional</strong>. Su color agrega el estado de sus nodos internos: verde si todo está listo, amarillo si hay Fase 1 en curso, rojo si aún está pendiente. Haz clic para explorar los nodos.</>
            ) : (
              <>Cada bloque es un <strong>sistema externo</strong> con el que Alfa-Field intercambia datos. Las flechas <span className="font-mono">punteadas</span> indican integraciones aún no cableadas.</>
            )}
          </div>
        </div>
      </div>

      {/* ─── Drill-down modal ─────────────────────────────────────── */}
      {drill && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { setDrill(null); setSelectedInternal(null); }} data-testid="drill-modal">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="px-6 py-4 flex items-center gap-4" style={{ background: drill.isHub ? "#0A3747" : STATUS_META[drill.status].bg }}>
              <button onClick={() => { setDrill(null); setSelectedInternal(null); }} className={`p-2 rounded-md ${drill.isHub ? "hover:bg-white/10 text-white" : "hover:bg-white/60 text-gray-700"}`} data-testid="drill-back-btn">
                <ArrowLeft size={18} />
              </button>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: drill.isHub ? "#E0E621" : STATUS_META[drill.status].color }}>
                {(() => {
                  const NI = drill.icon || Server;
                  return <NI size={22} color={drill.isHub ? "#0A3747" : "#fff"} strokeWidth={2.2} />;
                })()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-mono"
                  style={{ color: drill.isHub ? "#E0E621" : STATUS_META[drill.status].color }}>
                  {drill.isHub ? "Core del sistema" : STATUS_META[drill.status].label} · {drill.internal.length} nodos
                </div>
                <div className={`font-bold text-lg leading-tight ${drill.isHub ? "text-white" : "text-gray-900"}`}>
                  {drill.label}
                </div>
                <div className={`text-sm mt-1 ${drill.isHub ? "text-white/80" : "text-gray-600"}`}>
                  {drill.description}
                </div>
              </div>
              <button onClick={() => { setDrill(null); setSelectedInternal(null); }} className={`p-2 rounded-md ${drill.isHub ? "hover:bg-white/10 text-white" : "hover:bg-white/60 text-gray-700"}`} data-testid="drill-close-btn">
                <X size={20} />
              </button>
            </div>

            {/* Internal nodes: mini-graph + panel */}
            <div className="flex-1 overflow-y-auto bg-[#F8FAFC]">
              <MiniGraph
                nodes={drill.internal}
                selectedId={selectedInternal?.id}
                onSelect={setSelectedInternal}
              />

              {selectedInternal && (
                <div className="px-6 pb-4">
                  <div
                    className="rounded-lg border-2 p-4"
                    style={{
                      background: STATUS_META[selectedInternal.status].bg,
                      borderColor: STATUS_META[selectedInternal.status].border,
                    }}
                    data-testid="internal-node-detail"
                  >
                    <div className="flex items-start gap-3">
                      {(() => {
                        const { Icon, color } = STATUS_META[selectedInternal.status];
                        return (
                          <div
                            className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{ background: color }}
                          >
                            <Icon size={20} color="#fff" strokeWidth={2.4} />
                          </div>
                        );
                      })()}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: STATUS_META[selectedInternal.status].color, color: "#fff" }}
                          >
                            {STATUS_META[selectedInternal.status].label}
                          </span>
                          <span className="text-[10px] font-mono text-gray-500">ID: {selectedInternal.id}</span>
                        </div>
                        <div className="font-semibold text-gray-900 text-base mt-1">{selectedInternal.label}</div>
                        <p className="text-sm text-gray-700 leading-relaxed mt-2">{selectedInternal.detail}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {drill.relatedFlows && drill.relatedFlows.length > 0 && (
                <div className="px-6 pb-6" data-testid="related-flows-section">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-2">
                    Flujos de decisión relacionados
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {drill.relatedFlows.map((flow) => (
                      <button
                        key={flow.id}
                        onClick={() => {
                          setDrill(null);
                          setSelectedInternal(null);
                          navigate(`/flujos#${flow.id}`);
                        }}
                        data-testid={`related-flow-${flow.id}`}
                        className="text-left rounded-lg border-2 border-[#0A3747] bg-white hover:bg-[#0A3747] hover:text-white group px-4 py-3 flex items-center justify-between gap-3 transition-all"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-md bg-[#E0E621] flex items-center justify-center flex-shrink-0">
                            <Workflow size={16} color="#0A3747" strokeWidth={2.2} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-[#0A3747] group-hover:text-[#E0E621]">
                              Flujo de decisión
                            </div>
                            <div className="text-sm font-semibold leading-tight truncate">{flow.title}</div>
                          </div>
                        </div>
                        <ExternalLink size={16} className="flex-shrink-0 opacity-60 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-6 py-3 border-t border-gray-100 bg-white flex items-center justify-between text-xs text-gray-500 font-mono">
              <div>
                {drill.internal.filter((n) => n.status === S.DONE).length} operativos ·{" "}
                {drill.internal.filter((n) => n.status === S.WIP).length} en progreso ·{" "}
                {drill.internal.filter((n) => n.status === S.PENDING).length} pendientes
              </div>
              <div>ID: <span className="text-gray-800">{drill.id}</span></div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
        active ? "bg-[#0A3747] text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}

function StatBox({ label, value, tone }) {
  const color = tone && STATUS_META[tone] ? STATUS_META[tone].color : "#0A3747";
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-mono">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Chivo", color }}>{value}</div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}

// ─── Mini-grafo del drill-down ─────────────────────────────────────────────
// Auto-layout: 4 columnas por fila, edges sequenciales con curvas suaves.
const MINI_NODE_W = 190;
const MINI_NODE_H = 68;
const MINI_COL_GAP = 60;
const MINI_ROW_GAP = 40;
const MINI_COLS = 4;
const MINI_PAD_X = 40;
const MINI_PAD_Y = 30;

function layoutMiniNodes(nodes) {
  return nodes.map((n, i) => {
    const col = i % MINI_COLS;
    const row = Math.floor(i / MINI_COLS);
    return {
      ...n,
      _x: MINI_PAD_X + col * (MINI_NODE_W + MINI_COL_GAP) + MINI_NODE_W / 2,
      _y: MINI_PAD_Y + row * (MINI_NODE_H + MINI_ROW_GAP) + MINI_NODE_H / 2,
      _row: row,
    };
  });
}

function miniEdgePath(a, b) {
  // Misma fila → curva horizontal simple
  if (a._row === b._row) {
    const x1 = a._x + MINI_NODE_W / 2;
    const x2 = b._x - MINI_NODE_W / 2;
    const y = a._y;
    const mx = (x1 + x2) / 2;
    return `M ${x1} ${y} C ${mx} ${y}, ${mx} ${y}, ${x2} ${y}`;
  }
  // Cambio de fila → sale a la derecha, baja, vuelve a la izquierda
  const x1 = a._x + MINI_NODE_W / 2;
  const y1 = a._y;
  const x2 = b._x - MINI_NODE_W / 2;
  const y2 = b._y;
  const rightPad = 30;
  const outX = x1 + rightPad;
  return `M ${x1} ${y1} L ${outX} ${y1} C ${outX + 20} ${y1}, ${outX + 20} ${y2}, ${outX} ${y2} L ${x2} ${y2}`;
}

function MiniGraph({ nodes, selectedId, onSelect }) {
  const laidOut = useMemo(() => layoutMiniNodes(nodes), [nodes]);
  const rows = Math.ceil(nodes.length / MINI_COLS);
  const width = MINI_PAD_X * 2 + MINI_COLS * MINI_NODE_W + (MINI_COLS - 1) * MINI_COL_GAP + 60;
  const height = MINI_PAD_Y * 2 + rows * MINI_NODE_H + (rows - 1) * MINI_ROW_GAP;

  return (
    <div className="px-6 pt-5 pb-3" data-testid="mini-graph">
      <div className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-3">
        Flujo interno de nodos · click en cualquier nodo
      </div>
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <div
          className="relative"
          style={{
            width,
            height,
            minWidth: "100%",
            backgroundImage: "radial-gradient(circle, #E2E8F0 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        >
          <svg width={width} height={height} className="absolute inset-0 pointer-events-none">
            <defs>
              <marker id="mini-arrow" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748B" />
              </marker>
            </defs>
            {laidOut.slice(0, -1).map((a, i) => {
              const b = laidOut[i + 1];
              return (
                <path
                  key={i}
                  d={miniEdgePath(a, b)}
                  fill="none"
                  stroke="#64748B"
                  strokeWidth={1.6}
                  markerEnd="url(#mini-arrow)"
                  opacity={0.75}
                />
              );
            })}
          </svg>

          {laidOut.map((node) => {
            const meta = STATUS_META[node.status];
            const { Icon } = meta;
            const isSelected = selectedId === node.id;
            return (
              <button
                key={node.id}
                onClick={() => onSelect(isSelected ? null : node)}
                data-testid={`mini-node-${node.id}`}
                className="absolute rounded-lg text-left transition-all"
                style={{
                  left: node._x - MINI_NODE_W / 2,
                  top: node._y - MINI_NODE_H / 2,
                  width: MINI_NODE_W,
                  height: MINI_NODE_H,
                  background: meta.bg,
                  border: `2px solid ${isSelected ? "#0A3747" : meta.border}`,
                  boxShadow: isSelected
                    ? "0 6px 18px rgba(10,55,71,0.25)"
                    : "0 1px 3px rgba(0,0,0,0.08)",
                  transform: isSelected ? "scale(1.03)" : "scale(1)",
                }}
              >
                <div className="flex items-center gap-2 px-2.5 py-2 h-full">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: meta.color }}
                  >
                    <Icon size={14} color="#fff" strokeWidth={2.4} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500 leading-none">
                      {node.id}
                    </div>
                    <div
                      className="text-[12px] font-semibold text-gray-900 leading-tight mt-0.5"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {node.label}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
